import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useNavigate } from 'react-router-dom';
import { Button } from 'loplat-ui';
import { useTranslation } from 'react-i18next';
import { AuthUserApi } from '@api/index';
import TokenController from '@function/auth/tokenStorage';
import { reissueToken } from '@function/auth/token';
import { getTokenExpiry } from '@function/auth/tokenExpiry';
import userInfo from '@recoil/userInfo.atoms';
import * as S from './styles';

const ONE_MINUTE = 60 * 1000;
const FIVE_MINUTES = 5 * ONE_MINUTE;

type NudgeType = 'extend' | 'expired' | null;

const getTokenLeftTime = () => {
  const token = TokenController.get();
  if (!token?.access_token) return 0;
  return getTokenExpiry(token.access_token) - Date.now();
};

type PollerProps = {
  setNudge: Dispatch<SetStateAction<NudgeType>>;
  onExpired: () => void;
};

/** 토큰 잔여 시간 폴링 — user 있을 때만 마운트
 * - 1분 간격 + 탭 visibility 복귀 시 체크
 * - 만료 5분 전: setNudge('extend')
 * - 만료 시: onExpired() 호출 (로그아웃)
 */
const TokenExpiryPoller = ({ setNudge, onExpired }: PollerProps) => {
  useEffect(() => {
    const checkTokenStatus = () => {
      const token = TokenController.get();
      if (!token?.access_token) return;

      const timeLeft = getTokenLeftTime();
      if (timeLeft <= 0) {
        onExpired();
      } else if (timeLeft <= FIVE_MINUTES) {
        setNudge('extend');
      }
    };

    const interval = setInterval(checkTokenStatus, ONE_MINUTE);

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      checkTokenStatus();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [setNudge, onExpired]);

  return null;
};

type ModalProps = {
  nudgeType: NudgeType;
  onClose: () => void;
  onSignOut: () => void;
};

const TokenExpiryModal = ({ nudgeType, onClose, onSignOut }: ModalProps) => {
  const { t } = useTranslation('modal/session');

  if (!nudgeType) return null;

  const handleExtend = () => {
    const token = TokenController.get();
    if (!token) return onSignOut();
    reissueToken(token).then(onClose);
  };

  return (
    <S.Overlay>
      <S.ModalWrapper role='dialog' aria-modal='true'>
        <S.ModalBody>
          <p>{nudgeType === 'extend' ? t('extendAccess') : t('expired')}</p>
        </S.ModalBody>
        <S.Footer>
          {nudgeType === 'extend' ? (
            <Button color='primary' onClick={handleExtend}>
              {t('extend')}
            </Button>
          ) : (
            <Button color='primary' onClick={onClose}>
              {t('confirm')}
            </Button>
          )}
        </S.Footer>
      </S.ModalWrapper>
    </S.Overlay>
  );
};

const NudgeExtendToken = () => {
  const user = useRecoilValue(userInfo);
  const setUser = useSetRecoilState(userInfo);
  const navigate = useNavigate();
  const [nudgeType, setNudgeType] = useState<NudgeType>(null);

  const closeModal = () => setNudgeType(null);

  const signOut = () => {
    AuthUserApi.logout();
    setUser(null);
    setNudgeType('expired');
    navigate('/login');
  };

  return (
    <>
      {user && <TokenExpiryPoller setNudge={setNudgeType} onExpired={signOut} />}
      <TokenExpiryModal nudgeType={nudgeType} onClose={closeModal} onSignOut={signOut} />
    </>
  );
};

export default NudgeExtendToken;
